import { colors } from '$lib/config/colors';

// Utility functions for applying theme colors - Updated for red logo theme
export const theme = {
  // Button variants
  button: {
    primary: () => `relative overflow-hidden ${colors.components.button.primary} ${colors.components.button.primaryHover} shadow-2xl ${colors.components.button.shadow} transition-all duration-300 transform hover:scale-105 before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700`,
    
    secondary: () => `${colors.components.button.secondary} ${colors.components.button.secondaryHover} border border-white/20 hover:border-white/30 transition-all duration-200`,
    
    accent: () => `bg-gradient-to-r from-red-600 to-red-600 hover:from-red-600 hover:to-red-800 shadow-lg shadow-red-600/25 transition-all duration-300 transform hover:scale-105`,
    
    outline: () => `border-2 border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700 transition-all duration-200`,
  },
  
  // Text variants (bright mode)
  text: {
    gradient: () => `bg-gradient-to-r ${colors.gradients.text} bg-clip-text text-transparent`,
    gradientSecondary: () => `bg-gradient-to-r ${colors.gradients.textSecondary} bg-clip-text text-transparent`,
    gradientAccent: () => `bg-gradient-to-r ${colors.gradients.textAccent} bg-clip-text text-transparent`,
    primary: () => 'text-gray-900',
    secondary: () => 'text-gray-700',
    muted: () => 'text-gray-600',
    accent: () => colors.components.accent.text,
    brand: () => 'text-red-700',
    brandSecondary: () => 'text-red-600',
  },
  
  // Background variants (bright mode)
  background: {
    primary: () => 'bg-white',
    card: () => `${colors.components.card.background} ${colors.components.card.backdrop}`,
    cardAccent: () => `${colors.components.card.accent} ${colors.components.card.backdrop}`,
    glow: () => `bg-gradient-to-r ${colors.gradients.background}`,
    glowSecondary: () => `bg-gradient-to-r ${colors.gradients.backgroundSecondary}`,
    glowTertiary: () => `bg-gradient-to-r ${colors.gradients.backgroundTertiary}`,
    accent: () => colors.components.accent.background,
    hero: () => `bg-gradient-to-br ${colors.gradients.hero}`,
  },
  
  // Border variants (bright mode)
  border: {
    primary: () => colors.components.card.border,
    light: () => 'border-gray-200',
    accent: () => colors.components.accent.border,
    brand: () => 'border-red-200',
    brandStrong: () => 'border-red-300',
  },
  
  // Component-specific themes (bright mode)
  header: () => `${colors.components.header.background} supports-[backdrop-filter]:bg-white/80 ${colors.components.header.border}`,

  headerAccent: () => `${colors.components.header.accent} supports-[backdrop-filter]:bg-red-50/50`,
  
  footer: () => `${colors.components.footer.background} ${colors.components.footer.border}`,
  
  footerAccent: () => `${colors.components.footer.accent} ${colors.components.footer.border}`,
  
  badge: () => `${colors.components.badge.background} backdrop-blur-sm ${colors.components.badge.border} ${colors.components.badge.text}`,
  
  badgeAccent: () => `${colors.components.badge.accent} backdrop-blur-sm`,
  
  svg: {
    primary: () => colors.components.svg.primary,
    secondary: () => colors.components.svg.secondary,
    accent: () => colors.components.svg.accent,
    indicator: () => `${colors.components.svg.indicator} backdrop-blur-sm border rounded-lg px-3 py-1 text-xs font-mono`,
  },
  
  // Enhanced accent components for special sections
  accent: {
    card: () => `${colors.components.card.background} ${colors.components.card.backdrop} ${colors.components.accent.border}`,
    cardOverlay: () => colors.components.accent.cardOverlay,
    highlight: () => `${colors.components.accent.background} ${colors.components.accent.border} rounded-xl`,
    highlightSecondary: () => `${colors.components.accent.secondary} ${colors.components.accent.border} rounded-xl`,
    highlightTertiary: () => `${colors.components.accent.tertiary} ${colors.components.accent.border} rounded-xl`,
    hover: () => colors.components.accent.hover,
  },
  
  // Status indicators
  status: {
    success: () => colors.components.status.success,
    warning: () => colors.components.status.warning,
    error: () => colors.components.status.error,
    info: () => colors.components.status.info,
  },
  
  // Theme variants for different sections
  hero: {
    primary: () => colors.themes.hero.primary,
    secondary: () => colors.themes.hero.secondary,
    accent: () => colors.themes.hero.accent,
  },
  
  tech: {
    primary: () => colors.themes.tech.primary,
    secondary: () => colors.themes.tech.secondary,
    accent: () => colors.themes.tech.accent,
  },
  
  professional: {
    primary: () => colors.themes.professional.primary,
    secondary: () => colors.themes.professional.secondary,
    accent: () => colors.themes.professional.accent,
  },
};

// Helper function to combine multiple theme classes
export const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

// Color palette for direct access
export { colors } from '$lib/config/colors'; 